/**
 * GameStore - MobX store for game state and content pack management.
 *
 * Holds all registries, manages content pack loading, and controls the game tick loop.
 */

import { ENTITLEMENTS, type EntitlementKey } from '@idle-depths/shared';
import * as Sentry from '@sentry/react';
import { makeAutoObservable, runInAction } from 'mobx';

import { registerAllPacks } from '../../content-packs';

import type { RootStore } from './RootStore';
import type { PackContext } from '../../content-packs/types';

import { TickManager, type OfflineProgressResult } from '@/lib/TickManager';
import { ContentRegistry } from '@/registry/ContentRegistry';
import { EventBus } from '@/registry/EventBus';
import { PackLoader } from '@/registry/PackLoader';
import { SystemRegistry, type GameContext } from '@/registry/SystemRegistry';
import { UIRegistry } from '@/registry/UIRegistry';
import { LocalStorageService } from '@/services/LocalStorageService';
import { createLocationSystem } from '@/systems/LocationSystem';
import { createSkillsSystem } from '@/systems/SkillsSystem';

/**
 * GameStore manages the game's content, systems, and tick loop.
 */
export class GameStore {
  /** Content registry */
  readonly content: ContentRegistry;

  /** System registry */
  readonly systems: SystemRegistry;

  /** Event bus */
  readonly events: EventBus;

  /** UI registry */
  readonly ui: UIRegistry;

  /** Pack loader */
  readonly packLoader: PackLoader;

  /** Tick manager for game loop */
  readonly tickManager: TickManager;

  /** Whether content packs have been loaded */
  packsLoaded = false;

  /** Loading state */
  loading = false;

  /** Error state */
  error: Error | null = null;

  /** Whether the tick loop is currently enabled */
  isTickingEnabled = false;

  /** Last offline progress result (for display in dialog) */
  lastOfflineResult: OfflineProgressResult | null = null;

  /** Game speed multiplier (1.0 = normal, 2.0 = 2x speed, etc.) - Debug only */
  gameSpeedMultiplier = 1.0;

  /** Whether game is paused by debug panel */
  isDebugPaused = false;

  /** Reference to root store */
  private readonly rootStore: RootStore;

  /** Last time character was saved for playtime persistence (ms since epoch) */
  private lastCharacterSaveTime = 0;

  /** Interval for saving character data including playtime (5 seconds) */
  private readonly CHARACTER_SAVE_INTERVAL = 5000;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    // Create registries
    this.content = new ContentRegistry();
    this.systems = new SystemRegistry();
    this.events = new EventBus();
    this.ui = new UIRegistry();
    this.packLoader = new PackLoader();

    // Create tick manager
    this.tickManager = new TickManager({
      tickInterval: 100, // 10 ticks per second
      maxBatchTime: 1000, // 1 second max per batch
      maxOfflineTime: 4 * 60 * 60 * 1000, // 4 hours max offline
      persistInterval: 5000, // Save every 5 seconds
      sleepThreshold: 5000, // Detect sleep after 5 seconds
      onTick: (deltaMilliseconds) => {
        this.handleTick(deltaMilliseconds);
      },
      onOfflineProgress: (deltaMilliseconds) => {
        return this.handleOfflineProgress(deltaMilliseconds);
      },
    });

    // Register all known packs
    registerAllPacks(this.packLoader);

    makeAutoObservable(this, {
      // Mark readonly properties as not observable
      content: false,
      systems: false,
      events: false,
      ui: false,
      packLoader: false,
      tickManager: false,
    });
  }

  /**
   * Initialize the game store.
   *
   * Loads content packs based on user entitlements.
   * Note: Does NOT start ticking - call startTicking() after character selection.
   */
  async initialize(): Promise<void> {
    // Skip if already loaded (normal case)
    if (this.packsLoaded) {
      return;
    }

    // Handle HMR: if registry is locked, packs were already loaded in a previous cycle
    // This can happen when module hot-reloads but state is out of sync
    if (this.content.isLocked) {
      runInAction(() => {
        this.packsLoaded = true;
      });
      return;
    }

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      // Get user entitlements
      const entitlements = this.getEntitlements();

      // Create pack context
      const context: PackContext = {
        content: this.content,
        systems: this.systems,
        events: this.events,
        ui: this.ui,
      };

      // Load all packs
      await this.packLoader.loadAll(entitlements, context);

      // Register store-dependent systems (need rootStore access)
      // These are registered after content packs but before initialization
      if (!this.systems.has('location')) {
        this.systems.register(createLocationSystem(this.rootStore));
      }

      if (!this.systems.has('skills')) {
        this.systems.register(createSkillsSystem(this.rootStore));
      }

      // Initialize systems (skip if already initialized - HMR protection)
      if (!this.systems.isInitialized) {
        const gameContext: GameContext = {
          content: this.content,
          systems: this.systems,
          events: this.events,
          ui: this.ui,
        };
        await this.systems.initializeAll(gameContext);
      }

      runInAction(() => {
        this.packsLoaded = true;
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      runInAction(() => {
        this.error = err;
      });
      Sentry.captureException(error, {
        tags: { context: 'game_store_initialize' },
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /**
   * Start the game tick loop for a character.
   *
   * Call this when a character is selected and the game should start processing.
   * Automatically checks for and processes offline progress.
   *
   * @param characterId - The character ID to start ticking for
   */
  startTicking(characterId: string): void {
    if (this.isTickingEnabled) {
      return;
    }

    // Check for offline time for this character
    const offlineResult = this.tickManager.checkOfflineTime(characterId);
    if (offlineResult) {
      runInAction(() => {
        this.lastOfflineResult = offlineResult;
      });

      // Emit event for UI to show dialog
      if (offlineResult.showDialog) {
        this.events.emit('game:offline-progress', {
          durationMilliseconds: offlineResult.totalOfflineTime,
          processedMilliseconds: offlineResult.processedTime,
          wasCapped: offlineResult.wasCapped,
          characterId,
        });
      }
    }

    // Start the tick loop
    this.tickManager.start(characterId);

    runInAction(() => {
      this.isTickingEnabled = true;
    });
  }

  /**
   * Stop the game tick loop.
   *
   * Call this when the character is deselected or the game view is closed.
   */
  stopTicking(): void {
    if (!this.isTickingEnabled) {
      return;
    }

    this.tickManager.stop();

    runInAction(() => {
      this.isTickingEnabled = false;
      this.lastOfflineResult = null;
    });
  }

  /**
   * Clear the last offline result (after user dismisses dialog).
   */
  clearOfflineResult(): void {
    runInAction(() => {
      this.lastOfflineResult = null;
    });
    this.tickManager.clearLastOfflineResult();
  }

  /**
   * Handle a game tick.
   *
   * Only processes if there's an active character and game is not paused.
   * Applies the game speed multiplier to the delta time.
   *
   * @param deltaMilliseconds - Time since last tick in milliseconds
   */
  private handleTick(deltaMilliseconds: number): void {
    // Skip if debug paused
    if (this.isDebugPaused) {
      return;
    }

    // Only tick if we have an active character
    const activeCharacter = this.rootStore.characterStore.activeCharacter;
    if (!activeCharacter) {
      return;
    }

    // Apply speed multiplier
    const adjustedDelta = deltaMilliseconds * this.gameSpeedMultiplier;

    // Update total playtime (convert ms to seconds)
    activeCharacter.totalPlayTime += adjustedDelta / 1000;

    // Tick all systems
    this.systems.tick(this.getGameContext(), adjustedDelta);

    // Periodically save character data (including playtime)
    const now = Date.now();
    if (now - this.lastCharacterSaveTime >= this.CHARACTER_SAVE_INTERVAL) {
      this.lastCharacterSaveTime = now;
      activeCharacter.updatedAt = now;
      // Access private method via bracket notation (same pattern as SkillsSystem)
      this.rootStore.characterStore['saveCharacterToLocalStorage'](activeCharacter);
    }
  }

  /**
   * Handle offline progress notification.
   *
   * @param deltaMilliseconds - Total offline time processed
   * @returns Whether to show the offline progress dialog
   */
  private handleOfflineProgress(deltaMilliseconds: number): boolean {
    // Show dialog if offline for more than 1 minute
    return deltaMilliseconds > 60000;
  }

  /**
   * Get current entitlements from the entitlements store.
   */
  private getEntitlements(): Set<EntitlementKey> {
    // Always include base game
    const entitlements = new Set<EntitlementKey>([ENTITLEMENTS.BASE_GAME]);

    // Add any purchased entitlements
    for (const entitlement of this.rootStore.entitlementsStore.entitlementList) {
      entitlements.add(entitlement);
    }

    return entitlements;
  }

  /**
   * Get the game context for systems.
   */
  getGameContext(): GameContext {
    return {
      content: this.content,
      systems: this.systems,
      events: this.events,
      ui: this.ui,
    };
  }

  // ==========================================================================
  // Debug Controls
  // ==========================================================================

  /**
   * Set the game speed multiplier (debug only).
   *
   * @param multiplier - Speed multiplier (0.1 to 10)
   */
  setGameSpeed(multiplier: number): void {
    runInAction(() => {
      this.gameSpeedMultiplier = Math.max(0.1, Math.min(10, multiplier));
    });
  }

  /**
   * Set whether the game is paused (debug only).
   *
   * @param paused - Whether to pause the game
   */
  setDebugPaused(paused: boolean): void {
    runInAction(() => {
      this.isDebugPaused = paused;
    });
  }

  /**
   * Process a single tick manually (debug only, for step mode).
   *
   * Only works when the game is paused. Applies the speed multiplier.
   *
   * @param deltaMilliseconds - Time to process (default 100ms = 1 normal tick)
   */
  debugStepTick(deltaMilliseconds: number = 100): void {
    if (!this.isDebugPaused) {
      return;
    }

    const activeCharacter = this.rootStore.characterStore.activeCharacter;
    if (!activeCharacter) {
      return;
    }

    const adjustedDelta = deltaMilliseconds * this.gameSpeedMultiplier;
    this.systems.tick(this.getGameContext(), adjustedDelta);
  }

  /**
   * Skip forward by processing time instantly (debug only).
   *
   * Bypasses the pause check so time skip works even when paused.
   * Processes time in batches to avoid UI freeze.
   * Applies the current speed multiplier to the skip amount.
   *
   * @param milliseconds - Amount of time to skip forward
   */
  debugSkipTime(milliseconds: number): void {
    const activeCharacter = this.rootStore.characterStore.activeCharacter;
    if (!activeCharacter) {
      return;
    }

    // Apply speed multiplier to the skip amount
    const adjustedMilliseconds = milliseconds * this.gameSpeedMultiplier;

    // Process in batches directly to systems (bypasses pause check)
    const BATCH_SIZE = 1000; // 1 second per batch
    let remaining = adjustedMilliseconds;

    while (remaining > 0) {
      const batchTime = Math.min(remaining, BATCH_SIZE);
      this.systems.tick(this.getGameContext(), batchTime);
      remaining -= batchTime;
    }
  }

  /**
   * Reset the store.
   */
  reset(): void {
    // Stop ticking first
    this.stopTicking();

    // Dispose and reset all registries
    this.systems.dispose();
    this.content.reset();
    this.systems.reset();
    this.events.reset();
    this.ui.reset();
    this.packLoader.reset();

    // Clear all tick times
    LocalStorageService.clearAllTickTimes();

    // Re-register packs for next load
    registerAllPacks(this.packLoader);

    runInAction(() => {
      this.packsLoaded = false;
      this.loading = false;
      this.error = null;
      this.isTickingEnabled = false;
      this.lastOfflineResult = null;
      this.gameSpeedMultiplier = 1.0;
      this.isDebugPaused = false;
    });
  }

  /**
   * Dispose the store and clean up resources.
   */
  dispose(): void {
    this.stopTicking();
    this.tickManager.dispose();
    this.systems.dispose();
  }
}
